import React, { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import Select from '../../../../components/Selects/SimpleSelect';
import PrimaryButton from '../../../../components/Buttons/PrimaryButton';
import { ArrowBackIcon, CrossIcon } from '../../../../assets/svg/SVGComponents';

import {
  ArenaCard,
  FieldGroup,
  FieldLabel,
  FormButtonsRow,
  Tag,
  TagsRow,
} from './StepJudges.styles';
import { FormCard, StepTitle } from '../styles';
import { ITournamentSetupData } from '..';
import { IUser } from '../../../../hooks/auth';

interface IJudgeRecord {
  id: string;
  tournament_id: string;
  user_id: string;
  created_at: string;
  user: Pick<IUser, 'id' | 'name' | 'email' | 'avatar_url'>;
}

interface IAvailableJudge {
  id: string;
  name: string;
  email: string;
  avatar_url: string | null;
}

interface IStepJudgesProps {
  tournamentData: ITournamentSetupData;
  onSave: (tournament: ITournamentSetupData) => void;
  onCancel: () => void;
}

const StepJudges: React.FC<IStepJudgesProps> = ({ tournamentData, onSave }) => {
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);
  const [availableJudges, setAvailableJudges] = useState<IAvailableJudge[]>([]);
  const [assignedJudges, setAssignedJudges] = useState<IJudgeRecord[]>([]);
  const [selectValue, setSelectValue] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  useEffect(() => {
    if (!tournamentData.id) {
      setIsLoading(false);
      return;
    }
    Promise.all([
      api.get<IJudgeRecord[]>(`/tournaments/${tournamentData.id}/judges`),
      api.get<IAvailableJudge[]>(
        `/tournaments/${tournamentData.id}/judges/available`,
      ),
    ])
      .then(([assignedRes, availableRes]) => {
        setAssignedJudges(assignedRes.data ?? []);
        setAvailableJudges(availableRes.data ?? []);
      })
      .catch(() =>
        addToast({
          type: 'error',
          expression: 'Erro!',
          description: 'Não foi possível carregar a lista de juízes.',
        }),
      )
      .finally(() => setIsLoading(false));
  }, [tournamentData.id]);

  const options = availableJudges.map(u => ({
    value: u.id,
    label: u.name,
  }));

  const handleAdd = async (option: { value: string; label: string } | null) => {
    if (!option) return;
    const judge = availableJudges.find(u => u.id === option.value);
    if (!judge) return;

    setLoadingAction(`add-${judge.id}`);
    setSelectValue(null);
    try {
      await api.put(`/tournaments/${tournamentData.id}/judges`, {
        action: 'add',
        judge_ids: [judge.id],
      });
      const newRecord: IJudgeRecord = {
        id: judge.id,
        tournament_id: tournamentData.id ?? '',
        user_id: judge.id,
        created_at: new Date().toISOString(),
        user: judge,
      };
      setAssignedJudges(prev => [...prev, newRecord]);
      setAvailableJudges(prev => prev.filter(u => u.id !== judge.id));
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: `Não foi possível adicionar ${judge.name}.`,
      });
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRemove = async (record: IJudgeRecord) => {
    setLoadingAction(`remove-${record.id}`);
    try {
      await api.put(`/tournaments/${tournamentData.id}/judges`, {
        action: 'remove',
        judge_ids: [record.user_id],
      });
      setAssignedJudges(prev => prev.filter(j => j.id !== record.id));
      setAvailableJudges(prev => [
        ...prev,
        {
          id: record.user_id,
          name: record.user.name,
          email: record.user.email,
          avatar_url: record.user.avatar_url,
        },
      ]);
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: `Não foi possível remover ${record.user.name}.`,
      });
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <FormCard>
      <StepTitle>Etapa 3: Juízes</StepTitle>

      <Form ref={formRef} onSubmit={() => onSave({ ...tournamentData })}>
        <ArenaCard>
          <FieldGroup>
            <FieldLabel>Adicionar juíz</FieldLabel>
            <Select
              name="judge_select"
              options={options}
              value={selectValue}
              onChange={(option: { value: string; label: string } | null) =>
                handleAdd(option)
              }
              placeholder={
                isLoading
                  ? 'Carregando professores...'
                  : options.length === 0
                    ? 'Nenhum professor disponível'
                    : 'Selecionar professor...'
              }
              isDisabled={isLoading || loadingAction !== null}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                control: (base: object) => ({
                  ...base,
                  height: '3.05rem',
                  minHeight: '3.05rem',
                }),
                menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </FieldGroup>

          {assignedJudges.length > 0 && (
            <FieldGroup>
              <FieldLabel>Juízes adicionados</FieldLabel>
              <TagsRow>
                {assignedJudges.map(record => (
                  <Tag key={record.id}>
                    {record.user.name}
                    <button
                      type="button"
                      onClick={() => handleRemove(record)}
                      disabled={loadingAction === `remove-${record.id}`}
                      aria-label={`Remover ${record.user.name}`}
                    >
                      <CrossIcon
                        width="0.7rem"
                        stroke="currentColor"
                        fill="none"
                        strokeWidth={2.5}
                      />
                    </button>
                  </Tag>
                ))}
              </TagsRow>
            </FieldGroup>
          )}
        </ArenaCard>

        <FormButtonsRow>
          <PrimaryButton
            type="button"
            onClick={() => onSave({ ...tournamentData })}
            disabled={loadingAction !== null}
          >
            Concluir
            <ArrowBackIcon
              width="1.22rem"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              style={{ transform: 'rotate(180deg)' }}
            />
          </PrimaryButton>
        </FormButtonsRow>
      </Form>
    </FormCard>
  );
};

export { StepJudges };
